import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Download, Package, Boxes, RefreshCw, MapPin, BarChart3, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const WMSReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('sfg');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assigningBundle, setAssigningBundle] = useState(null);
  
  // SFG Report Data
  const [sfgReport, setSfgReport] = useState([]);
  const [sfgSummary, setSfgSummary] = useState({});
  const [sfgCanonical, setSfgCanonical] = useState(null);
  
  // FG Report Data
  const [fgReport, setFgReport] = useState([]);
  const [fgSummary, setFgSummary] = useState({});

  // Picked Not Handed Over Report Data
  const [pickedNotHandedReport, setPickedNotHandedReport] = useState([]);
  const [pickedNotHandedSummary, setPickedNotHandedSummary] = useState({});

  useEffect(() => {
    if (activeTab === 'sfg') {
      fetchSFGReport();
    } else if (activeTab === 'fg') {
      fetchFGReport();
    } else if (activeTab === 'picked-not-handed') {
      fetchPickedNotHandedReport();
    }
  }, [activeTab]);

  const fetchSFGReport = async () => {
    // Canonical stock summary — fast single aggregation, load independently so it
    // is not blocked by the heavier per-item storage report below.
    apiClient.get('/wms/sfg/canonical-stock-summary')
      .then(r => setSfgCanonical(r.data))
      .catch(() => setSfgCanonical(null));
    try {
      setLoading(true);
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await apiClient.get(`/wms/reports/sfg-storage${params}`);
      setSfgReport(response.data.report || []);
      setSfgSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch SFG report');
    } finally {
      setLoading(false);
    }
  };

  const fetchFGReport = async () => {
    try {
      setLoading(true);
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await apiClient.get(`/wms/reports/fg-storage${params}`);
      setFgReport(response.data.report || []);
      setFgSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch FG report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPickedNotHandedReport = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/wms/dispatch/picked-not-handed-over');
      setPickedNotHandedReport(response.data.report || []);
      setPickedNotHandedSummary(response.data.summary || {});
    } catch (error) {
      toast.error('Failed to fetch picked-not-handed-over report');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (activeTab === 'sfg') {
      fetchSFGReport();
    } else if (activeTab === 'fg') {
      fetchFGReport();
    } else if (activeTab === 'picked-not-handed') {
      fetchPickedNotHandedReport();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExport = async (type) => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await apiClient.get(`/wms/reports/${type}-storage/export${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_storage_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      STORED: { color: 'bg-green-100 text-green-700', label: 'Stored' },
      RESERVED: { color: 'bg-blue-100 text-blue-700', label: 'Reserved' },
      IN_WIP: { color: 'bg-yellow-100 text-yellow-700', label: 'In WIP' },
      PICKED: { color: 'bg-purple-100 text-purple-700', label: 'Picked' }
    };
    const { color, label } = config[status] || { color: 'bg-gray-100 text-gray-700', label: status };
    return <Badge className={`${color} font-medium`}>{label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleAssignBundle = async (bundleCode) => {
    try {
      setAssigning(true);
      setAssigningBundle(bundleCode);
      const response = await apiClient.post(`/wms/dispatch/assign-bundle-to-staff?bundle_code=${encodeURIComponent(bundleCode)}`);
      toast.success(`Assigned ${bundleCode} to ${response.data.assignment?.assigned_to || 'next available staff'}`);
      fetchPickedNotHandedReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign bundle');
    } finally {
      setAssigning(false);
      setAssigningBundle(null);
    }
  };

  const handleAssignAll = async () => {
    try {
      setAssigning(true);
      const response = await apiClient.post('/wms/dispatch/assign-all-unassigned-bundles');
      toast.success(`Assigned ${response.data.assigned_count || 0} bundles to staff`);
      fetchPickedNotHandedReport();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign bundles');
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">WMS Reports</h1>
              <p className="text-sm text-[#E8DCC8]">Storage inventory reports</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <TabsList className="bg-white border border-[#E5DFD0]">
              <TabsTrigger value="sfg" className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white">
                <Boxes className="w-4 h-4 mr-2" />
                SFG Storage
              </TabsTrigger>
              <TabsTrigger value="fg" className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white">
                <Package className="w-4 h-4 mr-2" />
                FG Storage
              </TabsTrigger>
              <TabsTrigger value="picked-not-handed" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
                <Package className="w-4 h-4 mr-2" />
                Picked Not Handed
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-2">
              <div className="relative flex-1 md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Search by SKU, design, bundle, location..."
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button onClick={handleSearch} variant="outline" data-testid="search-btn">
                <Search className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => handleExport(activeTab)}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* SFG Storage Tab */}
          <TabsContent value="sfg" className="space-y-6">
            {/* Canonical Stock-by-Status panel (accurate, full-collection grouping) */}
            {sfgCanonical?.buckets && (
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4" data-testid="sfg-canonical-panel">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-[#1A1F2E]">SFG Stock by Status</p>
                    <p className="text-xs text-[#5A5F6E]">
                      Canonical grouping across all {sfgCanonical.totals?.bundles?.toLocaleString() || 0} bundles ·
                      Available stock: <span className="font-semibold text-green-700">{sfgCanonical.available_stock_pieces?.toLocaleString() || 0}</span> pcs
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {sfgCanonical.buckets
                    .filter(b => b.canonical !== 'EXCEPTION' || b.bundles > 0)
                    .map(b => {
                      const styles = {
                        STORED: 'bg-green-50 border-green-200 text-green-700',
                        ALLOCATED: 'bg-blue-50 border-blue-200 text-blue-700',
                        PICKED: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                        IN_WIP: 'bg-amber-50 border-amber-200 text-amber-700',
                        TRANSFERRED: 'bg-slate-50 border-slate-200 text-slate-700',
                        EXCEPTION: 'bg-red-50 border-red-200 text-red-700',
                      }[b.canonical] || 'bg-gray-50 border-gray-200 text-gray-700';
                      const rawList = Object.entries(b.raw_statuses || {})
                        .sort((a, c) => c[1].bundles - a[1].bundles)
                        .map(([k, v]) => `${k}: ${v.bundles.toLocaleString()}`)
                        .join(' · ');
                      return (
                        <div key={b.canonical} className={`rounded-lg border p-3 ${styles}`} data-testid={`sfg-canonical-${b.canonical}`}>
                          <p className="text-xs font-medium">{b.label}</p>
                          <p className="text-2xl font-bold leading-tight">{b.pieces.toLocaleString()}</p>
                          <p className="text-xs opacity-80">{b.bundles.toLocaleString()} bundles</p>
                          {rawList && (
                            <p className="text-[10px] opacity-70 mt-1 leading-snug break-words" title={rawList}>{rawList}</p>
                          )}
                        </div>
                      );
                    })}
                </div>
                <p className="text-[10px] text-[#8A8F9E] mt-2">
                  Buckets: STORED (available) · ALLOCATED (reserved/ready) · PICKED (picked/processed/consumed for Dhoti/Combo) ·
                  IN_WIP (true work-in-progress) · TRANSFERRED · EXCEPTION (missing/returned/written-off — not counted as stock)
                </p>
              </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <p className="text-sm text-[#5A5F6E]">Total Bundles</p>
                <p className="text-2xl font-bold text-[#1A1F2E]">{sfgSummary.total_bundles || 0}</p>
              </div>
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <p className="text-sm text-[#5A5F6E]">Available USNs</p>
                <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="sfg-summary-available">{sfgSummary.total_usns || 0}</p>
                {sfgSummary.total_consumed > 0 && (
                  <p className="text-xs text-gray-400 mt-0.5">of {sfgSummary.total_usns_nominal || 0} nominal · {sfgSummary.total_consumed} consumed</p>
                )}
              </div>
              <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                <p className="text-sm text-green-600">Stored</p>
                <p className="text-2xl font-bold text-green-700">{sfgSummary.stored_count || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <p className="text-sm text-blue-600">Reserved</p>
                <p className="text-2xl font-bold text-blue-700">{sfgSummary.reserved_count || 0}</p>
              </div>
              <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
                <p className="text-sm text-yellow-600">In WIP</p>
                <p className="text-2xl font-bold text-yellow-700">{sfgSummary.in_wip_count || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                <p className="text-sm text-purple-600">Unique SKUs</p>
                <p className="text-2xl font-bold text-purple-700">{sfgSummary.unique_skus || 0}</p>
              </div>
            </div>

            {/* SFG Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#5A5F6E]" />
                  <p className="mt-2 text-[#5A5F6E]">Loading...</p>
                </div>
              ) : sfgReport.length === 0 ? (
                <div className="p-12 text-center">
                  <Boxes className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
                  <p className="text-[#5A5F6E]">No SFG storage data found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Bundle Code</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">SKU Code</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Design Name</th>
                        <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]" title="Pieces physically on the rack (raw − consumed)">Available</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Storage Location</th>
                        <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Class</th>
                        <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Put Away Date</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Put Away By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sfgReport.map((row, index) => (
                        <tr key={row.sfg_id || index} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-[#1A1F2E]">{row.bundle_code}</td>
                          <td className="px-4 py-3 font-medium text-[#1A1F2E]">{row.sku_code}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.design_name}</td>
                          <td className="px-4 py-3 text-center font-mono">
                            <span className="font-semibold text-[#1A1F2E]">{row.available_count ?? row.usn_count}</span>
                            {row.consumed_count > 0 && (
                              <span
                                className="ml-1 text-xs text-gray-400"
                                title={`Nominal: ${row.usn_count} · Consumed: ${row.consumed_count}`}
                              >
                                /{row.usn_count}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-[#5A5F6E]">
                              <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate max-w-[200px]" title={row.location_path}>
                                {row.bin_location || row.location_path}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={`${
                              row.velocity_class === 'A' ? 'bg-green-100 text-green-700' :
                              row.velocity_class === 'B' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {row.velocity_class}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(row.status)}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{formatDate(row.put_away_date)}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.put_away_by || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* FG Storage Tab */}
          <TabsContent value="fg" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
                <p className="text-sm text-[#5A5F6E]">Total Packs</p>
                <p className="text-2xl font-bold text-[#1A1F2E]">{fgSummary.total_packs || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg border border-green-200 p-4">
                <p className="text-sm text-green-600">Stored</p>
                <p className="text-2xl font-bold text-green-700">{fgSummary.stored_count || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                <p className="text-sm text-purple-600">Picked</p>
                <p className="text-2xl font-bold text-purple-700">{fgSummary.picked_count || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                <p className="text-sm text-blue-600">Unique SKUs</p>
                <p className="text-2xl font-bold text-blue-700">{fgSummary.unique_skus || 0}</p>
              </div>
              <div className="bg-orange-50 rounded-lg border border-orange-200 p-4">
                <p className="text-sm text-orange-600">Unique Bins</p>
                <p className="text-2xl font-bold text-orange-700">{fgSummary.unique_bins || 0}</p>
              </div>
            </div>

            {/* FG Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#5A5F6E]" />
                  <p className="mt-2 text-[#5A5F6E]">Loading...</p>
                </div>
              ) : fgReport.length === 0 ? (
                <div className="p-12 text-center">
                  <Package className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
                  <p className="text-[#5A5F6E]">No FG storage data found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Pack Barcode</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">SKU Code</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Design Name</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Bundle Code</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Bin Location</th>
                        <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Put Away Date</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Put Away By</th>
                        <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Picked For</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fgReport.map((row, index) => (
                        <tr key={row.fg_id || index} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-[#1A1F2E]">{row.pack_barcode}</td>
                          <td className="px-4 py-3 font-medium text-[#1A1F2E]">{row.sku_code}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.design_name}</td>
                          <td className="px-4 py-3 font-mono text-[#5A5F6E]">{row.bundle_code || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-[#5A5F6E]">
                              <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="truncate max-w-[200px]" title={row.location_path}>
                                {row.bin_code || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">{getStatusBadge(row.status)}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{formatDate(row.put_away_date)}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.put_away_by || '-'}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.picked_for_slip || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Picked Not Handed Over Tab */}
          <TabsContent value="picked-not-handed" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD0] shadow-sm">
                <p className="text-sm text-[#5A5F6E]">Total Bundles</p>
                <p className="text-2xl font-bold text-orange-600">{pickedNotHandedSummary.total_bundles || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD0] shadow-sm">
                <p className="text-sm text-[#5A5F6E]">Total Pieces</p>
                <p className="text-2xl font-bold text-orange-600">{pickedNotHandedSummary.total_pieces || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD0] shadow-sm">
                <p className="text-sm text-[#5A5F6E]">Assigned to Staff</p>
                <p className="text-2xl font-bold text-green-600">{pickedNotHandedSummary.assigned_to_staff || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD0] shadow-sm">
                <p className="text-sm text-[#5A5F6E]">Not Assigned</p>
                <p className="text-2xl font-bold text-red-600">{pickedNotHandedSummary.not_assigned || 0}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#E5DFD0] shadow-sm flex flex-col gap-2">
                <Button 
                  onClick={fetchPickedNotHandedReport} 
                  variant="outline" 
                  className="w-full"
                  disabled={loading}
                  data-testid="picked-not-handed-refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {(pickedNotHandedSummary.not_assigned || 0) > 0 && (
                  <Button 
                    onClick={handleAssignAll} 
                    className="w-full bg-[#2D5F4F] hover:bg-[#234A3E] text-white"
                    disabled={assigning}
                    data-testid="assign-all-unassigned-btn"
                  >
                    <Users className={`w-4 h-4 mr-2 ${assigning ? 'animate-spin' : ''}`} />
                    Assign All ({pickedNotHandedSummary.not_assigned})
                  </Button>
                )}
              </div>
            </div>

            {/* By Picker and By Assigned Staff Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* By Picker */}
              {pickedNotHandedSummary.by_picker?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="font-semibold text-orange-800 mb-3">Bundles by Picker (who picked)</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {pickedNotHandedSummary.by_picker.map((picker, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-orange-200 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-[#1A1F2E]">{picker.picker_name || picker.picker_id}</p>
                          <p className="text-xs text-gray-500">{picker.picker_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-orange-600">{picker.bundles} bundles</p>
                          <p className="text-xs text-gray-500">{picker.pieces} pcs • {picker.assigned || 0} assigned</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By Assigned Staff */}
              {pickedNotHandedSummary.by_assigned_staff?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h3 className="font-semibold text-green-800 mb-3">Bundles by Assigned Staff (piece processing)</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {pickedNotHandedSummary.by_assigned_staff.map((staff, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-green-200 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-[#1A1F2E]">{staff.staff_name || staff.staff_id}</p>
                          <p className="text-xs text-gray-500">{staff.staff_id}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-green-600">{staff.bundles} bundles</p>
                          <p className="text-xs text-gray-500">{staff.pieces} pcs</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Report Table */}
            <div className="bg-white rounded-xl shadow-sm border border-[#E5DFD0] overflow-hidden">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-[#2D5F4F]" />
                  <p className="text-[#5A5F6E]">Loading report...</p>
                </div>
              ) : pickedNotHandedReport.length === 0 ? (
                <div className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p className="text-[#5A5F6E]">No picked bundles pending handover</p>
                  <p className="text-sm text-gray-500">All picked bundles have been handed over to staff</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-orange-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Bundle Code</th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Picker</th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Pick Slip</th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Design</th>
                        <th className="px-4 py-3 text-center font-semibold text-orange-800">Pieces</th>
                        <th className="px-4 py-3 text-left font-semibold text-green-800">Assigned Staff</th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Status</th>
                        <th className="px-4 py-3 text-left font-semibold text-orange-800">Picked At</th>
                        <th className="px-4 py-3 text-center font-semibold text-orange-800">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickedNotHandedReport.map((row, index) => (
                        <tr key={row.bundle_code || index} className="border-b border-[#E5DFD0] hover:bg-orange-50/50">
                          <td className="px-4 py-3 font-mono font-medium text-[#1A1F2E]">{row.bundle_code}</td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-[#1A1F2E]">{row.picker_name}</p>
                              <p className="text-xs text-gray-500">{row.picker_id}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-[#5A5F6E]">{row.slip_no}</td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{row.design_name || row.sku_code || '-'}</td>
                          <td className="px-4 py-3 text-center font-semibold text-orange-600">{row.usn_count}</td>
                          <td className="px-4 py-3">
                            {row.is_assigned ? (
                              <div>
                                <p className="font-medium text-green-700">{row.assigned_staff_name}</p>
                                <p className="text-xs text-gray-500">{row.assigned_staff_id}</p>
                              </div>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Not Assigned</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={row.is_assigned ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {row.assignment_status || row.slip_status || 'Picked'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-[#5A5F6E]">{formatDate(row.picked_at)}</td>
                          <td className="px-4 py-3 text-center">
                            {!row.is_assigned ? (
                              <Button
                                size="sm"
                                className="bg-[#2D5F4F] hover:bg-[#234A3E] text-white"
                                disabled={assigning && assigningBundle === row.bundle_code}
                                onClick={() => handleAssignBundle(row.bundle_code)}
                                data-testid={`assign-bundle-btn-${row.bundle_code}`}
                              >
                                {assigning && assigningBundle === row.bundle_code ? (
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <UserPlus className="w-3 h-3 mr-1" />
                                )}
                                Assign
                              </Button>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">Assigned</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default WMSReports;
